import * as Opt from 'fp-ts/Option';
import { pipe } from 'fp-ts/lib/function';
import * as Ord from 'fp-ts/Ord';
import { sortBy, findFirst } from 'fp-ts/Array';
import * as N from 'fp-ts/number';
import { startOfDay } from 'date-fns';
import { Option } from 'fp-ts/Option';
import { option } from 'fp-ts';
import {
  FroInsightsData,
  FroMessageMediums,
  MessageMediumByTimeData,
  MessageMediumByTimeMetric,
  LiftKeyMap,
  LiftMetric,
  PerformanceOverTimeLift,
  FrequencyPeriod,
} from 'modules/intelligentInsights/frequencyOptimization/types';
import { MessageMedium } from 'constants/messageMediums';
import {
  DataPoint,
  MessageMediumGraphDataPoints,
} from 'modules/intelligentInsights/frequencyOptimization/components/performanceOverTime/types';
import { MESSAGE_MEDIUM_COLORS } from 'modules/intelligentInsights/frequencyOptimization/components/performanceOverTime/constants';
import { match } from 'ts-pattern';
import { OptimizedChangeMetric } from 'modules/intelligentInsights/frequencyOptimization/types';
import {
  formatAsCSVDate,
  formatAsFullDate,
  getInsightsHistory,
  ordByDate,
  truncateNumber,
} from 'modules/intelligentInsights/domain.utils';
import {
  INCREASE,
  DECREASE,
  IN,
  OPTIMIZED_LIFT_TEXT,
  OPTIMIZED_METRIC_TEXT,
  OPTIMIZED_METRIC_NO_CHANGE,
  OPTIMIZED_LIFT_NO_CHANGE,
} from 'modules/intelligentInsights/constants';
import { isNotNull } from 'utils/tsHelpers';
import { parseDate } from 'utils/datetime';
import { FREQUENCY_PERIOD } from 'modules/intelligentInsights/frequencyOptimization/components/performanceOverTime/constants';

export const filterMediumByLiftForGraph = (
  selectedLiftTab: PerformanceOverTimeLift,
  data: MessageMediumGraphDataPoints,
) => {
  const messageMediumsToExcludeByLift: FroMessageMediums[] = match(selectedLiftTab)
    .with(PerformanceOverTimeLift.Open, () => [MessageMedium.SMS] as FroMessageMediums[])
    .with(PerformanceOverTimeLift.Click, () => [MessageMedium.Push] as FroMessageMediums[])
    .with(PerformanceOverTimeLift.Unsubscribe, () => [])
    .exhaustive();

  const filteredData = { ...data };

  messageMediumsToExcludeByLift.forEach((messageMediumToExclude) => {
    delete filteredData[messageMediumToExclude];
  });

  return filteredData;
};

export const getMessageMediumByTimeByFrequencyPeriod = (
  metrics: Option<MessageMediumByTimeMetric[]>,
  targetFrequencyPeriod: FrequencyPeriod,
): Option<MessageMediumByTimeData[]> => {
  if (option.isNone(metrics)) {
    return Opt.none;
  }

  return pipe(
    metrics,
    Opt.chain((metricsArray) => {
      return pipe(
        metricsArray,
        findFirst((metric) => {
          const matches = metric.frequencyPeriod === targetFrequencyPeriod;
          return matches;
        }),
        Opt.map((metric) => metric.data),
      );
    }),
  );
};

export const transformDataforSeriesGraph = (
  input: MessageMediumByTimeMetric[],
  liftType: PerformanceOverTimeLift,
) => {
  const liftKey = LiftKeyMap[liftType] || LiftKeyMap.Open;
  const weeklyFrequencyPeriodMessageByMediumData = getMessageMediumByTimeByFrequencyPeriod(
    Opt.some(input),
    FREQUENCY_PERIOD.WEEKLY,
  );

  const data = Opt.getOrElse(() => [] as MessageMediumByTimeData[])(
    weeklyFrequencyPeriodMessageByMediumData,
  );

  const grouped = data.reduce<Record<MessageMedium, DataPoint[]>>(
    (acc, item) => {
      if (!acc[item.messageMedium]) {
        acc[item.messageMedium] = [];
      }
      const date = startOfDay(parseDate(item.endDate));
      acc[item.messageMedium].push({
        date: formatAsFullDate(date),
        x: date.getTime(),
        y: item[liftKey],
      });
      return acc;
    },
    {} as Record<MessageMedium, DataPoint[]>,
  );

  const filteredByLift: Record<FroMessageMediums, DataPoint[]> = filterMediumByLiftForGraph(
    liftType,
    grouped,
  );

  const result = Object.entries(filteredByLift)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([key, values]) => ({
      name: key,
      data: values.sort((a, b) => a.x - b.x),
      color: MESSAGE_MEDIUM_COLORS[key as FroMessageMediums],
    }));

  return result;
};

export const shouldShowValue = (value: number | null) => {
  return isNotNull(value) && value !== 0;
};

export const getBiggestLiftText = (
  count: number,
  messageMedium: FroMessageMediums,
  changeMetric: LiftMetric,
) => {
  if (!shouldShowValue(count)) {
    return OPTIMIZED_LIFT_NO_CHANGE;
  }

  const directionalityText = count > 0 ? INCREASE : DECREASE;
  return `${directionalityText} ${IN} ${messageMedium} ${OPTIMIZED_LIFT_TEXT[changeMetric]}`;
};

export const getBiggestChangeText = (
  count: number,
  messageMedium: FroMessageMediums,
  changeMetric: OptimizedChangeMetric,
) => {
  if (!shouldShowValue(count)) {
    return OPTIMIZED_METRIC_NO_CHANGE;
  }

  const directionalityText = count > 0 ? INCREASE : DECREASE;
  return `${directionalityText} ${IN} ${messageMedium} ${OPTIMIZED_METRIC_TEXT[changeMetric]}`;
};

const WeightedMessageMedium = {
  Email: 1,
  Push: 2,
  SMS: 3,
};
const ordByWeightMessageMedium = Ord.fromCompare(
  (a: MessageMediumByTimeData, b: MessageMediumByTimeData) =>
    N.Ord.compare(WeightedMessageMedium[a.messageMedium], WeightedMessageMedium[b.messageMedium]),
);

type FormattedPerformanceItem = {
  endDate: string;
  messageMedium: MessageMedium;
  optimizedOpenRate: string | number;
  optimizedClickRate: string | number;
  optimizedUnsubscribeRate: string | number;
  frequencyPeriod: number;
};

export const sortByDateAndMedium = sortBy([ordByDate, ordByWeightMessageMedium]);

export const derivePerformanceDataForExport = (
  mediumByTime: MessageMediumByTimeMetric[],
): FormattedPerformanceItem[] => {
  return mediumByTime.flatMap((frequencyPeriodData) => {
    const frequencyPeriod = frequencyPeriodData.frequencyPeriod;

    if (!frequencyPeriodData.data) {
      return [];
    }

    const sortedFrequencyPeriodData = sortByDateAndMedium(frequencyPeriodData.data);
    return sortedFrequencyPeriodData.map((historyItem) => ({
      endDate: formatAsCSVDate(historyItem.endDate),
      messageMedium: historyItem.messageMedium,
      optimizedOpenRate:
        historyItem.optimizedOpenRate === null
          ? ''
          : truncateNumber(historyItem.optimizedOpenRate, 4),
      optimizedClickRate:
        historyItem.optimizedClickRate === null
          ? ''
          : truncateNumber(historyItem.optimizedClickRate, 4),
      optimizedUnsubscribeRate:
        historyItem.optimizedUnsubscribeRate === null
          ? ''
          : truncateNumber(historyItem.optimizedUnsubscribeRate, 4),
      frequencyPeriod: frequencyPeriod,
    }));
  });
};

export const getPerformanceDataExport = (froInsightsData: FroInsightsData) => {
  return pipe(
    froInsightsData,
    getInsightsHistory,
    Opt.getOrElse(() => [] as MessageMediumByTimeMetric[]),
    derivePerformanceDataForExport,
  );
};
